import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import { Button, Checkbox, Dialog, Divider, IconButton, MenuItem, Stack, TextField, Typography, Tooltip, Box } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CellColorCircle } from './components/CellColorCircle';
import { CellIconButton } from './components/CellIconButton';
import { StyledSelect } from '../StyledSelect/StyledSelect';
import { closeConsultationReminderModal, openConsultationReminderModal } from 'FRONTEND/reducers/consultationSlice';
import { RootState } from 'FRONTEND/reducers/types';
import { selectHasRight } from 'FRONTEND/reducers/userSlice';
import { Consultation, useEditConsultationRemindersMutation } from 'FRONTEND/utils/backendApi/Consultations/consultationsApi';
import { REMINDER_COLORS } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { RIGHTS } from 'FRONTEND/utils/rights';

const ReminderModal = ({ row, showColorAndNotes }: { row: Consultation, showColorAndNotes: boolean }) => {
  const dispatch = useDispatch();
  const open = useSelector((state: RootState) => state.consultation.consultationReminderModalOpenId === row.consultation_id);

  const { links, reminder_at, notes, color_code, is_testing } = row;

  const hasTestConsultationRight = useSelector((state: RootState) => selectHasRight(state, RIGHTS.CONSULTATIONS_TEST));

  // Endpoint for updating a consultation (contains patientId and consultationId for given consultation) ex: patients/40/consultations/42/reminder/
  const reminderUrl = links.find(link => link.rel === 'reminder')?.href;

  const [sharedNotes, setSharedNotes] = useState(notes || '');
  const [date, setDate] = useState<Dayjs | null>(reminder_at ? dayjs(reminder_at) : null);
  const [isTestingConsultation, setIsTestingConsultation] = useState(is_testing);

  const [selectedColor, setSelectedColor] = useState(color_code || 0);

  const [editConsultationReminders] = useEditConsultationRemindersMutation();

  const handleUpdateConsultationReminders = () => {
    editConsultationReminders({
      reminderUrl,
      reminderAt: date ? date.toISOString() : null,
      colorCode: selectedColor,
      notes: sharedNotes,
      is_testing: isTestingConsultation,
    });
    dispatch(closeConsultationReminderModal());
  };

  return (
    <>
      <Dialog open={open} onClose={() => dispatch(closeConsultationReminderModal())} sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' }, '& .MuiDialog-paper': { borderRadius: '16px', maxWidth: '600px', width: '100%', marginTop: '80px' } }}>
        <Stack width='100%'>
          <Stack spacing={1} p={2}>
            {showColorAndNotes && (
              <>
                {/* Notes input */}
                <>
                  <Typography variant='h6' fontWeight='bold'>{i18n._('Shared notes')}</Typography>
                  <TextField multiline minRows={2} value={sharedNotes} onChange={(e) => setSharedNotes(e.target.value)} fullWidth sx={{ '& textarea': { resize: 'vertical' } }} />
                </>
                {/* Color selector input */}
                <>
                  <Typography variant='h6' fontWeight='bold'>{i18n._('Color code')}</Typography>
                  <StyledSelect value={selectedColor} onChange={(e) => setSelectedColor(e.target.value as number)} sx={{ width: 100 }}>
                    {Object.entries(REMINDER_COLORS).map(([color, hex]) => (
                      <MenuItem key={color} value={color} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Stack direction='row' alignItems='center' spacing={1} p={1}>
                          {color && <CellColorCircle color={color} />}
                          {/* <Typography>{i18n._(color) || i18n._('None')}</Typography> */}
                        </Stack>
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </>
              </>
            )}


            {/* Date selector input */}
            <>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack width='50%' direction='row' alignItems='center' pt={1} spacing={1}>
                  <DateTimePicker
                    value={date}
                    onChange={(date) => setDate(date)}
                    slotProps={{ textField: { placeholder: i18n._('Personal reminder'), size: 'small' } }}
                  />
                  <IconButton onClick={() => setDate(null)}><CancelIcon /></IconButton>
                </Stack>
              </LocalizationProvider>
            </>
            {/* Testing consultation checkbox */}
            {hasTestConsultationRight && (
              <Stack direction='row' alignItems='center' pt={1} spacing={1}>
                <Checkbox
                  checked={isTestingConsultation}
                  onChange={(e) => setIsTestingConsultation(e.target.checked)}
                  sx={{ padding: 0 }}
                />
                <Box position='relative' display='inline-flex'>
                  <Typography variant='h6' fontWeight='bold'>{i18n._('Functional or technical test')}</Typography>
                  <Tooltip title={i18n._('Corresponds to a functional or technical test. Do not use for medical diagnosis.')} slotProps={{ popper: { sx: { zIndex: 3000 } } }}>
                    <InfoIcon color='action' sx={{ position: 'absolute', top: -6, right: -18, fontSize: '16px' }} />
                  </Tooltip>
                </Box>
              </Stack>
            )}
          </Stack>
          {/* CTA buttons */}
          <Divider sx={{ width: '100%' }} />
          <Stack direction='row' spacing={2} p={2} justifyContent='flex-end'>
            <Button
              variant='contained'
              onClick={() => dispatch(closeConsultationReminderModal())}
              sx={{
                backgroundColor: (theme) => theme.palette.grey[100],
                color: (theme) => theme.palette.grey[800],
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.grey[300],
                },
              }}
            >
              {i18n._('Cancel')}
            </Button>
            <Button variant='contained' onClick={handleUpdateConsultationReminders}>{i18n._('Confirm')}</Button>
          </Stack>
        </Stack>
      </Dialog>
      <CellIconButton icon={<AddCircleIcon />} onClick={() => dispatch(openConsultationReminderModal(row.consultation_id))} />
    </>
  );
};

export default ReminderModal;
